import {
  COMMON_FIELDS,
  COMMON_INSTRUMENT_FIELDS,
  EVENT_TYPE_FIELD_MAP,
  OPTION_BEARING_ACTION_TYPES,
} from '../models/ice-field-mappings'
import type { EventTypeEntry } from '../models/ice-field-mappings'

export interface DetailRow {
  property: string
  value: string
}

export function getNested(obj: Record<string, unknown>, path: string): unknown {
  const segments = path.split('.')
  let current: unknown = obj
  for (const segment of segments) {
    if (current == null || typeof current !== 'object') {
      return undefined
    }
    const arrayMatch = segment.match(/^(.+)\[(\d+)\]$/)
    if (arrayMatch) {
      const key = arrayMatch[1]
      const index = Number(arrayMatch[2])
      const arr = (current as Record<string, unknown>)[key]
      if (!Array.isArray(arr)) {
        return undefined
      }
      current = arr[index]
    } else {
      current = (current as Record<string, unknown>)[segment]
    }
  }
  return current
}

export function getInstrumentCode(instrument: unknown, scheme: string): string {
  if (instrument == null || typeof instrument !== 'object') {
    return ''
  }
  const inst = instrument as Record<string, unknown>
  const codeField = inst['code']
  if (codeField == null) {
    return ''
  }

  const codes: unknown[] = Array.isArray(codeField) ? codeField : [codeField]

  for (const entry of codes) {
    if (entry != null && typeof entry === 'object') {
      const record = entry as Record<string, unknown>
      if (record['scheme'] === scheme) {
        const val = record['value']
        return val == null ? '' : String(val)
      }
    }
  }
  return ''
}

function toDisplayString(val: unknown): string {
  if (val == null) {
    return ''
  }
  return String(val)
}

const warnedCategories = new Set<string>()

/**
 * Pure variant for testing: callers supply the map so tests can inject stubs.
 * Lookup key is event_type normalized to trim().toLowerCase().
 */
export function extractEventSubtypeFromMap(
  rawData: Record<string, unknown>,
  eventType: string,
  map: Record<string, EventTypeEntry>,
): string | null | undefined {
  const key = eventType.trim().toLowerCase()
  const entry = map[key]
  if (entry === undefined) {
    return undefined
  }
  if (!entry.subtypePath) {
    return null
  }
  const value = getNested(rawData, entry.subtypePath)
  if (value == null) {
    return null
  }
  const str = String(value)
  return str === '' ? null : str
}

/**
 * Extracts the Ice sub-type value for the given event category from rawData,
 * using the canonical EVENT_TYPE_FIELD_MAP (R1.7 contract).
 *
 * Returns:
 *   undefined — unrecognized category (+ deduped console.warn per session); also undefined
 *               for absent/empty event_type (null/undefined/whitespace) without warning
 *   null      — recognized category, no subtypePath configured, or value absent/empty
 *   string    — extracted sub-type value
 */
export function extractEventSubtype(
  rawData: Record<string, unknown>,
  eventType: string | null | undefined,
): string | null | undefined {
  const trimmed = String(eventType == null ? '' : eventType).trim()
  if (trimmed === '') {
    return undefined
  }

  const result = extractEventSubtypeFromMap(
    rawData,
    trimmed,
    EVENT_TYPE_FIELD_MAP,
  )

  if (result === undefined) {
    const lookupKey = trimmed.toLowerCase()
    if (!warnedCategories.has(lookupKey)) {
      warnedCategories.add(lookupKey)
      console.warn(
        `[IceCorporateActions] Unrecognized Event Category: "${trimmed}"`,
      )
    }
  }

  return result
}

export function extractIceDetailRows(
  rawData: Record<string, unknown>,
): DetailRow[] {
  const rows: DetailRow[] = []

  for (const field of COMMON_FIELDS) {
    rows.push({
      property: field.label,
      value: toDisplayString(getNested(rawData, field.path)),
    })
  }

  for (const field of COMMON_INSTRUMENT_FIELDS) {
    const instrument = getNested(rawData, field.instrumentPath)
    rows.push({
      property: field.label,
      value: getInstrumentCode(instrument, field.scheme),
    })
  }

  const eventType = toDisplayString(rawData['event_type']).trim().toLowerCase()
  // Exact match first, then startsWith-prefix fallback — matches the registry-aware
  // variants (extractIceDetailWithOptions / extractIceDetailRowsWithRegistry) so the
  // public API resolves family-level subtypes consistently (e.g. 'tender offer buyback'
  // resolves to the 'tender offer' entry).
  const typeConfig = registryLookup(eventType, EVENT_TYPE_FIELD_MAP)
  if (typeConfig) {
    for (const field of typeConfig.fields) {
      rows.push({
        property: field.label,
        value: toDisplayString(getNested(rawData, field.path)),
      })
    }
    for (const field of typeConfig.instrumentFields) {
      const instrument = getNested(rawData, field.instrumentPath)
      rows.push({
        property: field.label,
        value: getInstrumentCode(instrument, field.scheme),
      })
    }
  }

  return rows
}

export interface OptionGroup {
  optionNumber: number
  rows: DetailRow[]
}

export interface IceDetailWithOptions {
  mode: 'flat' | 'options'
  eventLevelRows: DetailRow[]
  /**
   * Invariant (enforced by extractIceDetailWithOptions):
   *   - mode === 'options' implies optionGroups is present and length > 1
   *     (single-option events fall back to mode === 'flat').
   *   - optionGroups, when present, is sorted ascending by optionNumber.
   * Downstream consumers (e.g. the detail component's optionsMatrix) rely on
   * the sort order — change the extractor with care.
   */
  optionGroups?: OptionGroup[]
  sectionHeader?: string
}

// ---------------------------------------------------------------------------
// Named helpers extracted from extractIceDetailWithOptions (I-9 refactor)
// ---------------------------------------------------------------------------

/**
 * Registry lookup: exact match first, then startsWith-prefix fallback.
 *
 * Prefix matching enables family-level grouping: 'tender offer' covers
 * 'tender offer buyback', 'tender offer rights', etc.
 *
 * INVARIANT: registry keys must form a non-overlapping prefix set (no key may be
 * a prefix of another). Violating this would make the fallback non-deterministic
 * — Object.entries iteration order would pick whichever entry came first. This
 * invariant is enforced by a self-consistency unit test against EVENT_TYPE_FIELD_MAP.
 */
function registryLookup(
  normalized: string,
  registry: Record<string, EventTypeEntry>,
): EventTypeEntry | undefined {
  return (
    registry[normalized] ??
    Object.entries(registry).find(([key]) => normalized.startsWith(key))?.[1]
  )
}

/**
 * Returns true when action_type (normalized) is option-bearing AND the registry
 * entry has the required optionsArrayKey and optionFields configured.
 */
function shouldUseOptionsMode(
  actionTypeNormalized: string,
  entry: EventTypeEntry | undefined,
): boolean {
  const isOptionBearing = OPTION_BEARING_ACTION_TYPES.some(
    t => t.toLowerCase() === actionTypeNormalized,
  )
  return (
    isOptionBearing &&
    !!entry?.optionsArrayKey &&
    !!entry.optionFields &&
    entry.optionFields.length > 0
  )
}

/**
 * Deduplicates the raw options array by option_number.
 * - Items with null/missing option_number are skipped with a warn.
 * - Items with empty string option_number are skipped with a warn.
 * - Duplicate option_numbers keep the first occurrence (warn on duplicates).
 *
 * Returns the deduplicated array sorted ascending by option_number. Downstream
 * consumers (e.g. the detail component's optionsMatrix transposition) rely on
 * this ascending order — change with care.
 *
 * First-wins on duplicates is intentional: ICE data quality issues should be
 * surfaced to the console without breaking the UI. If a downstream consumer
 * needs different behavior, copy this helper rather than parameterizing it.
 */
// Exported for direct unit testing of the validation/sort/dedup branches.
export function deduplicateOptions(
  arr: unknown[],
  optionsArrayKey: string,
): Array<Record<string, unknown>> {
  const seen = new Set<number>()
  const dedup: Array<Record<string, unknown>> = []
  for (const item of arr) {
    if (item == null || typeof item !== 'object') continue
    const rec = item as Record<string, unknown>
    // Validate option_number is present and non-empty before coercing to Number.
    // Number('') === 0 which is finite, causing all missing-option_number items to
    // collide as "option 0" and silently drop all but the first.
    if (rec['option_number'] == null) {
      console.warn(
        `[IceCorporateActions] Option item in "${optionsArrayKey}" is missing option_number; skipping`,
      )
      continue
    }
    const optStr = toDisplayString(rec['option_number'])
    if (optStr === '') {
      console.warn(
        `[IceCorporateActions] Option item in "${optionsArrayKey}" has empty option_number; skipping`,
      )
      continue
    }
    const optNum = Number(optStr)
    if (!Number.isInteger(optNum) || optNum <= 0) {
      console.warn(
        `[IceCorporateActions] Non-positive-integer option_number="${optStr}" in "${optionsArrayKey}"; skipping`,
      )
      continue
    }
    if (seen.has(optNum)) {
      console.warn(
        `[IceCorporateActions] Duplicate option_number=${optNum} in "${optionsArrayKey}"; rendering first occurrence`,
      )
      continue
    }
    seen.add(optNum)
    dedup.push(rec)
  }
  dedup.sort((a, b) => Number(a['option_number']) - Number(b['option_number']))
  return dedup
}

/**
 * AMET0-3440 slice 4a — resolves subtype overrides against a live payload.
 *
 * If `baseEntry.subtypeOverrides` is absent or empty, returns `baseEntry`
 * unchanged (same reference — callers can use referential equality to detect
 * the no-op path, though correctness does not depend on it).
 *
 * When a matching override is found the resolved entry is a new object via
 * shallow spread: `{ ...baseEntry, ...override }`. This is top-level key
 * replacement — no recursive merge. The caller owns the resolved entry;
 * mutating it is safe and will not affect the registry.
 *
 * Exported for direct unit testing (same pattern as deduplicateOptions /
 * buildOptionGroups).
 */
export function resolveEntry(
  baseEntry: EventTypeEntry,
  payload: Record<string, unknown>,
): EventTypeEntry {
  const overrides = baseEntry.subtypeOverrides
  if (!overrides || Object.keys(overrides).length === 0) {
    return baseEntry
  }
  if (!baseEntry.subtypePath) {
    // subtypeOverrides without subtypePath is a misconfiguration guarded by
    // the self-test invariant in ice-field-mappings.spec.ts. Fall back safely.
    return baseEntry
  }
  const rawValue = getNested(payload, baseEntry.subtypePath)
  if (rawValue == null) {
    return baseEntry
  }
  const subtypeLower = String(rawValue).trim().toLowerCase()
  if (subtypeLower === '') {
    return baseEntry
  }
  const override = overrides[subtypeLower]
  if (!override) {
    return baseEntry
  }
  return { ...baseEntry, ...override }
}

/**
 * Builds OptionGroup rows for each deduplicated option.
 * Only populated (non-null, non-empty) fields are included per option.
 */
// Exported for direct unit testing of per-option field projection.
export function buildOptionGroups(
  dedup: Array<Record<string, unknown>>,
  entry: EventTypeEntry,
): OptionGroup[] {
  return dedup.map(opt => {
    const rows: DetailRow[] = []
    for (const field of entry.optionFields ?? []) {
      const v = getNested(opt, field.path)
      if (v == null || v === '') continue
      rows.push({ property: field.label, value: toDisplayString(v) })
    }
    for (const field of entry.optionInstrumentFields ?? []) {
      const instrument = getNested(opt, field.instrumentPath)
      const code = getInstrumentCode(instrument, field.scheme)
      if (code === '') continue
      rows.push({ property: field.label, value: code })
    }
    return { optionNumber: Number(opt['option_number']), rows }
  })
}

/**
 * Builds the event-level rows used in options mode: COMMON_FIELDS +
 * COMMON_INSTRUMENT_FIELDS + entry.eventLevelOnlyFields (fields that live at
 * the event root, not under the optionsArrayKey).
 *
 * Per-family fields that are option-level (i.e. paths under optionsArrayKey)
 * are intentionally excluded; they appear in OptionGroup rows via buildOptionGroups.
 * Families where all `fields` paths are option-scoped (e.g. M&A) will have
 * no eventLevelOnlyFields and only common fields appear here.
 */
function buildEventLevelRows(
  rawData: Record<string, unknown>,
  entry: EventTypeEntry,
): DetailRow[] {
  const rows: DetailRow[] = []
  for (const field of COMMON_FIELDS) {
    rows.push({
      property: field.label,
      value: toDisplayString(getNested(rawData, field.path)),
    })
  }
  for (const field of COMMON_INSTRUMENT_FIELDS) {
    const instrument = getNested(rawData, field.instrumentPath)
    rows.push({
      property: field.label,
      value: getInstrumentCode(instrument, field.scheme),
    })
  }
  for (const field of entry.eventLevelOnlyFields ?? []) {
    rows.push({
      property: field.label,
      value: toDisplayString(getNested(rawData, field.path)),
    })
  }
  return rows
}

/**
 * Shallow-copy `rawData` and overwrite the value at `path` with `value`.
 *
 * Only handles plain dotted paths (no `[N]` segment syntax) — caller's
 * responsibility to ensure that. Used by the length-1 unwrap below where
 * `path` is the registry's `optionsArrayKey`, which is invariably a plain
 * dotted slot (e.g. `capital_change.capital_change_details`).
 *
 * Returns rawData unchanged if any intermediate segment is missing or
 * non-object — the unwrap is best-effort.
 */
function setAtPath(
  rawData: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const segments = path.split('.')
  if (segments.some(s => /\[\d+\]/.test(s))) {
    // Defensive: bracket syntax not supported here. No registry entry uses it
    // in optionsArrayKey today; skip the unwrap rather than corrupt the payload.
    return rawData
  }
  // Defense-in-depth: refuse prototype-polluting segment names. The registry is
  // hardcoded today so this is not a live attack surface; guard is forward-looking
  // for any future data-driven registry path.
  if (
    segments.some(
      s => s === '__proto__' || s === 'constructor' || s === 'prototype',
    )
  ) {
    return rawData
  }
  const root: Record<string, unknown> = { ...rawData }
  let cursor: Record<string, unknown> = root
  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i]
    const next = cursor[seg]
    if (next == null || typeof next !== 'object' || Array.isArray(next)) {
      return rawData
    }
    const copy = { ...(next as Record<string, unknown>) }
    cursor[seg] = copy
    cursor = copy
  }
  cursor[segments[segments.length - 1]] = value
  return root
}

/**
 * AMET0-3440: input normalization at flat-mode entry.
 *
 * If the resolved registry entry has an `optionsArrayKey` and the value at
 * that slot is an array of length exactly 1 with an object element, returns
 * a shallow-copied rawData with that slot replaced by the [0] element. Any
 * other shape (object, missing, primitive, length 0, length > 1) returns
 * rawData unchanged.
 *
 * The substitution is local to flat-mode field resolution; the caller's
 * `action_type` and any other source fields are preserved verbatim. This is
 * symmetric with options mode's existing special-case at the same slot:
 * options mode iterates per-element (length > 1); flat mode resolves dotted
 * paths against [0] (length === 1).
 *
 * Resulting invariant for `optionsArrayKey` value:
 *   array, length > 1   → options mode (iterate per element)
 *   array, length === 1 → flat mode against [0] (this unwrap)
 *   array, length === 0 → flat mode raw (existing empty-cells behavior)
 *   object              → flat mode against object (existing)
 *   missing/null/primitive → flat mode raw (existing)
 *
 * If a second pre-extract mutation is needed in the future, consider hoisting into
 * a dedicated `normalizeRawData()` step rather than chaining additional unwrap calls.
 */
function maybeUnwrapSingleOptionArray(
  rawData: Record<string, unknown>,
  entry: EventTypeEntry | undefined,
): Record<string, unknown> {
  if (!entry?.optionsArrayKey) return rawData
  const arr = getNested(rawData, entry.optionsArrayKey)
  if (!Array.isArray(arr) || arr.length !== 1) return rawData
  const elem = arr[0]
  if (elem == null || typeof elem !== 'object' || Array.isArray(elem)) {
    return rawData
  }
  return setAtPath(
    rawData,
    entry.optionsArrayKey,
    elem as Record<string, unknown>,
  )
}

/**
 * Extends extractIceDetailRows with an "options mode" branch.
 *
 * When action_type matches OPTION_BEARING_ACTION_TYPES (case-insensitive) AND the
 * registry entry has an optionsArrayKey AND the resolved array length > 1 (after dedup),
 * returns grouped per-option rows and suppresses the per-family flat fields from eventLevelRows.
 *
 * Falls back to flat rendering for: missing/unrecognized action_type, object-shaped
 * optionsArrayKey value (silent — legitimate single-option data), primitive-shaped value
 * (warns), or option array length <= 1 after dedup.
 *
 * Length-1 unwrap (AMET0-3440): when the resolved entry has `optionsArrayKey`
 * pointing at an array of length 1, the slot is [0]-promoted in a shallow copy
 * before flat-mode field resolution so dotted paths walk into the option object
 * rather than the array. See `maybeUnwrapSingleOptionArray` for the full
 * invariant table.
 *
 * @param rawData   The ICE rawData object.
 * @param registry  The event type registry to use (defaults to EVENT_TYPE_FIELD_MAP;
 *                  injectable for testing AC-EXT-011 genericity).
 */
export function extractIceDetailWithOptions(
  rawData: Record<string, unknown>,
  registry: Record<string, EventTypeEntry> = EVENT_TYPE_FIELD_MAP,
): IceDetailWithOptions {
  const normalized = toDisplayString(rawData['event_type']).trim().toLowerCase()
  const entry = registryLookup(normalized, registry)
  // AMET0-3440 slice 4a: resolve subtype overrides before any downstream use.
  // No-op when the entry has no subtypeOverrides (all current registry entries).
  // resolvedEntry is used for every downstream consumer — fields, instrumentFields,
  // optionsArrayKey, optionFields, optionInstrumentFields, eventLevelOnlyFields.
  const resolvedEntry = entry ? resolveEntry(entry, rawData) : undefined
  const actionType = toDisplayString(rawData['action_type'])
  const actionTypeNormalized = actionType.trim().toLowerCase()
  // Length-1 unwrap is applied before flat-mode resolution. The options-mode
  // path below continues to read the original `rawData` so the dedup gate
  // sees the array shape (and rejects length === 1 → flat with unwrapped rows).
  const flatSourceData = maybeUnwrapSingleOptionArray(rawData, resolvedEntry)
  const flatRows = extractIceDetailRowsWithRegistry(
    flatSourceData,
    registry,
    resolvedEntry,
  )

  if (!shouldUseOptionsMode(actionTypeNormalized, resolvedEntry)) {
    return { mode: 'flat', eventLevelRows: flatRows }
  }

  // resolvedEntry is guaranteed non-undefined by shouldUseOptionsMode; the non-null
  // assertion is safe here — the guard above checks entry?.optionsArrayKey.
  const optionsArrayKey = resolvedEntry!.optionsArrayKey!
  const arr = getNested(rawData, optionsArrayKey)

  // Three shapes handled below: undefined warns (misconfiguration), primitive warns
  // (malformed input), null/object are legitimate single-option data and fall through silently.
  if (!Array.isArray(arr)) {
    if (arr === undefined) {
      // Path resolved to undefined: likely a misconfiguration (typo in optionsArrayKey)
      // rather than legitimate data. Warn to aid diagnosis.
      console.warn(
        `[IceCorporateActions] optionsArrayKey "${optionsArrayKey}" resolved to undefined; ` +
          `check registry configuration. Falling back to flat render.`,
      )
    } else if (arr != null && typeof arr !== 'object') {
      // Polymorphic data shape: ICE emits the same registry-key as an OBJECT for single-option /
      // mandatory shape and as an ARRAY for multi-option shape. An object value here is legitimate
      // single-option data, not malformed — fall back to flat silently. Only warn on primitives
      // (string, number, boolean) which indicate genuinely malformed input.
      console.warn(
        `[IceCorporateActions] Expected array or object at "${optionsArrayKey}" but got ${typeof arr}; falling back to flat render`,
      )
    }
    // null or object: legitimate single-option shape — silent fall-through.
    return { mode: 'flat', eventLevelRows: flatRows }
  }

  const dedup = deduplicateOptions(arr, optionsArrayKey)
  if (dedup.length <= 1) {
    return { mode: 'flat', eventLevelRows: flatRows }
  }

  const groups = buildOptionGroups(dedup, resolvedEntry!)
  const eventLevelRows = buildEventLevelRows(rawData, resolvedEntry!)
  // Section header preserves the literal action_type string from rawData (no display
  // normalization in v1; revisit if cross-event casing inconsistency is observed).
  const sectionHeader = `ELECTION OPTIONS · ${groups.length} options · ${actionType}`

  return {
    mode: 'options',
    eventLevelRows,
    optionGroups: groups,
    sectionHeader,
  }
}

/**
 * Internal variant of extractIceDetailRows that accepts a custom registry.
 * Used by extractIceDetailWithOptions to compute the flat fall-back rows.
 *
 * When `resolvedEntry` is supplied (from `resolveEntry` in the caller), it is
 * used directly for `fields` and `instrumentFields` instead of doing a fresh
 * registry lookup. This threads subtype overrides through flat-mode rendering
 * without changing the legacy `extractIceDetailRows` code path.
 */
function extractIceDetailRowsWithRegistry(
  rawData: Record<string, unknown>,
  registry: Record<string, EventTypeEntry>,
  resolvedEntry?: EventTypeEntry,
): DetailRow[] {
  const rows: DetailRow[] = []

  for (const field of COMMON_FIELDS) {
    rows.push({
      property: field.label,
      value: toDisplayString(getNested(rawData, field.path)),
    })
  }

  for (const field of COMMON_INSTRUMENT_FIELDS) {
    const instrument = getNested(rawData, field.instrumentPath)
    rows.push({
      property: field.label,
      value: getInstrumentCode(instrument, field.scheme),
    })
  }

  const eventType = toDisplayString(rawData['event_type']).trim().toLowerCase()
  const typeConfig = resolvedEntry ?? registryLookup(eventType, registry)
  if (typeConfig) {
    for (const field of typeConfig.fields) {
      rows.push({
        property: field.label,
        value: toDisplayString(getNested(rawData, field.path)),
      })
    }
    for (const field of typeConfig.instrumentFields) {
      const instrument = getNested(rawData, field.instrumentPath)
      rows.push({
        property: field.label,
        value: getInstrumentCode(instrument, field.scheme),
      })
    }
  }

  return rows
}
